import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from "react";
import classNames from 'classnames';
import { dateToISOFormat } from '@utils';
import { getInputSizeStyles, getInputStateClass } from '../utils';
import { _getTimeDetails } from './utils';
import Calendar from './Calendar/Calendar';
import Clock from './Clock/Clock';
import { isValid } from 'date-fns';
import { UXPContext } from "@core/context/Context";
import { Dropdown } from '@widget/popups/Dropdown';
import { FontAwesomeIconWrapper } from "@icon/fontawesome/FontAwesomeIconWrapper";
import DropdownIndicator from "@widget/other/DropdownIndicator";
import Input from '../Input';
/**
 *
 * @export
 *
 * This component is used to select a datetime.
 *
 * @example
 * ```
 *  <DateTimePicker
 *      title="Select Date & Time"
 *      datetime={date}
 *      onChange={(date) => { setDate(date); }}
 *  />
 * ```
 */
const DateTimePicker = (props) => {
    const context = React.useContext(UXPContext);
    // props
    let { title, datetime, onChange, options, disableInput, hideLabels, hideDateInput, showFullMonthName = true, dropdownClassname, minWidth, maxWidth, width, fullWidth, disabled, readOnly, isValid: isValidProp, dropdownMaxWidth, placeholder, closeOnSelect, onClear } = props;
    if (!title)
        title = context.$L('uxp-core.text.select-date-time');
    if (!placeholder)
        placeholder = context.$L('uxp-core.text.select-date-time');
    // states
    let [timeObj, setTimeObj] = React.useState({ hours: null, mins: null, secs: null, evening: false, date: null, timeString: null });
    let [showPicker, setShowPicker] = React.useState(false);
    let [dateValue, setDateValue] = React.useState(null);
    let [timeValue, setTimeValue] = React.useState(null);
    let [datetimeInputValue, setDatetimeInputValue] = React.useState('');
    let [manualValue, setManualValue] = React.useState('');
    const [isFocused, setIsFocused] = React.useState(false);
    const [showInput, setShowInput] = React.useState(false);
    const triggerRef = React.useRef(null);
    React.useEffect(() => {
        let _d = _getTimeDetails(datetime, true);
        setTimeObj(_d);
        setDateValue(_d.date);
        setTimeValue(_d.timeString);
        // Set combined datetime input value
        if (_d.date) {
            const combinedValue = `${dateToISOFormat(_d.date)} ${_d.timeString}`;
            setDatetimeInputValue(combinedValue);
            setManualValue(combinedValue);
        }
        else {
            setDatetimeInputValue('');
            setManualValue('');
        }
    }, [datetime]);
    const handleKeyDownOnTrigger = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setShowPicker(true);
        }
    };
    const handleTriggerClick = () => {
        if (!disabled && !readOnly) {
            setShowPicker(!showPicker);
        }
    };
    // Handle focus to show focus styles
    const handleFocus = () => {
        if (!disabled && !readOnly) {
            setIsFocused(true);
        }
    };
    // Handle blur to remove focus styles
    const handleBlur = () => {
        if (!disabled && !readOnly) {
            setIsFocused(false);
        }
    };
    const handleManualInputChange = (val) => {
        // Allow free-form editing, validation happens on blur
        setManualValue(val);
    };
    const handleManualInputBlur = () => {
        // Parse and validate on blur
        try {
            const _d = _getTimeDetails(manualValue, true);
            if (_d.date && isValid(_d.date)) {
                onChange(_d.date);
                // Re-format to standard format
                setManualValue(`${dateToISOFormat(_d.date)} ${_d.timeString}`);
            }
            else {
                // Invalid input, reset to current valid value
                setManualValue(datetimeInputValue);
            }
        }
        catch (e) {
            // Invalid input, reset to current valid value
            setManualValue(datetimeInputValue);
        }
    };
    const onChangeHandler = (range, time) => {
        let date = timeObj.date;
        if (range && range.start) {
            let _d = range.start;
            date = new Date(_d.getFullYear(), _d.getMonth(), _d.getDate(), date.getHours(), date.getMinutes(), date.getSeconds());
        }
        if (time) {
            date = new Date(date.getFullYear(), date.getMonth(), date.getDate(), time.getHours(), time.getMinutes(), time.getSeconds());
        }
        onChange(date);
    };
    const closePicker = () => {
        setShowPicker(false);
        setShowInput(false);
        setTimeout(() => { var _a; return (_a = triggerRef === null || triggerRef === void 0 ? void 0 : triggerRef.current) === null || _a === void 0 ? void 0 : _a.focus(); }, 100);
    };
    const renderDropdownContent = () => (_jsxs("div", { className: "uxpcore_datetimepicker__dropdown-content", children: [_jsxs("div", { className: "uxpcore_datetimepicker__header", children: [_jsx("div", { className: "uxpcore_datetimepicker__title", children: showInput ? context.$L('uxp-core.text.enter-date') : title }), !hideDateInput && (_jsx("div", { className: "uxpcore_datetimepicker__toggle-input", onClick: (e) => {
                            e.stopPropagation();
                            setShowInput(!showInput);
                        }, title: showInput ? context.$L('uxp-core.text.show-calendar') : context.$L('uxp-core.text.type-date'), children: _jsx(FontAwesomeIconWrapper, { icon: ['far', showInput ? 'calendar' : 'keyboard'] }) }))] }), showInput && !hideDateInput && (_jsx("div", { className: "uxpcore_datetimepicker__input-container", children: _jsx(Input, { type: "text", value: manualValue, onChange: handleManualInputChange, onBlur: handleManualInputBlur, readOnly: disableInput, placeholder: "YYYY-MM-DD HH:MM:SS AM/PM", fullWidth: true, hideClear: true }) })), !showInput && (_jsx("div", { className: "uxpcore_datetimepicker__calendar-container", children: _jsx(Calendar, Object.assign({ range: { start: timeObj.date, end: null }, onSelect: (range) => onChangeHandler(range) }, options, { showFullMonthName: showFullMonthName })) })), _jsx("hr", { className: "uxpcore_datetimepicker__divider" }), _jsx("div", { className: "uxpcore_datetimepicker__clock-container", children: _jsx(Clock, { time: timeObj.date, onChange: (date) => onChangeHandler(null, date), disableInput: disableInput }) }), _jsx("div", { className: "uxpcore_datetimepicker__footer", children: _jsx("button", { className: "uxpcore_datetimepicker__done-btn", onClick: closePicker, children: context.$L('uxp-core.text.done') }) })] }));
    const containerStyles = getInputSizeStyles({ minWidth, maxWidth, width, fullWidth, style: undefined });
    const dropdownIndicator = _jsx(DropdownIndicator, { isOpen: showPicker });
    const stateClass = getInputStateClass({ disabled, readOnly, isValid: isValidProp });
    const clearButton = (!!datetime && !disabled && !readOnly) ? (_jsx("div", { className: "uxpcore_input__clear-btn", onClick: (e) => { e.stopPropagation(); onClear ? onClear() : onChange(null); }, children: _jsx(FontAwesomeIconWrapper, { icon: ['fas', 'times'] }) })) : null;
    return (_jsxs("div", { className: classNames("uxpcore_datetimepicker", { "uxpcore_input--full-width": fullWidth }), style: containerStyles, children: [_jsxs("div", { ref: triggerRef, className: classNames("uxpcore_input", "uxpcore_input--datetimepicker", stateClass, {
                    "uxpcore_input--has-prefix": !hideLabels,
                    "uxpcore_input--has-suffix": !!(clearButton || dropdownIndicator),
                    "uxpcore_input--has-content": !!datetime,
                    "uxpcore_input--focus": isFocused || showPicker,
                    "uxpcore_input--full-width": fullWidth
                }), onClick: handleTriggerClick, onKeyDown: handleKeyDownOnTrigger, onFocus: handleFocus, onBlur: handleBlur, tabIndex: disabled || readOnly ? -1 : 0, style: getInputSizeStyles({ minWidth, maxWidth, width, fullWidth, style: undefined }), children: [!hideLabels && (_jsx("div", { className: "uxpcore_input__prefix", children: _jsx(FontAwesomeIconWrapper, { icon: ['fas', 'calendar'] }) })), _jsx("div", { className: "uxpcore_input__value", children: timeObj.date ? (_jsxs(_Fragment, { children: [_jsx("div", { className: "uxpcore_datetimepicker__date-display", children: _jsx("span", { children: dateToISOFormat(timeObj.date) }) }), _jsx("span", { className: "uxpcore_datetimepicker__separator", children: "\u2022" }), _jsx("div", { className: "uxpcore_datetimepicker__time-display", children: _jsx("span", { children: timeObj.timeString }) })] })) : (placeholder) }), _jsxs("div", { className: "uxpcore_input__suffix", children: [clearButton, dropdownIndicator] })] }), _jsx(Dropdown, { externalTriggerRef: triggerRef, content: renderDropdownContent(), position: "bottom-left", isOpen: showPicker, onToggle: closePicker, contentClassName: classNames("uxpcore_datetimepicker__dropdown", dropdownClassname), matchTriggerWidth: false, maxWidth: dropdownMaxWidth || '20rem', minWidth: "20rem" })] }));
};
export default DateTimePicker;
